import { Form } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import { AppProps } from 'presentation/App';
import { PageTitle } from 'presentation/components/PageTitle';

import { ChatHistoryQuery } from 'domain/entities/ChatEntity';
import { UserToken } from 'domain/entities/UserEntity';
import { openErrorNoti } from 'presentation/components/Notification';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { sendSocket } from 'presentation/redux/features/app/socketSlice';
import {
    completeRealoadMessage,
    setChatRoom,
} from 'presentation/redux/features/counter/chatCounterSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import ChatForm from './component/ChatFormComponent';
import ChatMessage from './component/ChatPageComponent';
import HeaderComponent from './component/HeaderComponent';
import { chatHelper } from './utils/ChatHelpers';

export interface MessageEntity {
    action: string;
    chatRoomId: number;
    chatMessageId: number | string;
    id?: string;
    chatMessage: string;
    memberId: string;
    memberType: number;
    memberName: string;
    sendAt: string;
    fileUrl: string;
    messageStatus: string;
    messageType: string | number;
}

export const CHAT_STATUS = {
    NOT_SEND: 'NotSend',
    SENDING: 'Sending',
    SEND: 'Send',
    READ: 'Read',
};

const DEFAULT_PAGE_INFO = {
    page: 1,
    pageSize: 30,
};

const CU01201Page = (props: AppProps) => {
    const [form] = Form.useForm();
    const [messageList, setMessageList] = useState<MessageEntity[]>([]);
    const [userLocal, setUserLocal] = useState<UserToken>();
    const [pageInfo, setPageInfo] = React.useState<any>(DEFAULT_PAGE_INFO);
    const [maxPage, setMaxPage] = React.useState<number>(-1);
    const dispatch = useAppDispatch();
    const userIdRef: any = useRef();
    const chatCounterSlice = useAppSelector((state) => state.chatCounterSlice);
    const focusRef = useRef<any>();

    //load when first render
    React.useEffect(() => {
        getUserInfo();
        loadChat(pageInfo);
    }, []);

    React.useEffect(() => {
        if (chatCounterSlice.needReload && chatCounterSlice.connected) {
            actionWhenSocketReceive();
        }
    }),
        [chatCounterSlice];

    const actionWhenSocketReceive = async () => {
        if (!chatCounterSlice.chatRoom || !userLocal?.user.id) return;
        if (chatCounterSlice.needReload && chatCounterSlice.payloadAction === 'sendMessage') {
            reloadChatData(DEFAULT_PAGE_INFO);
        }
        if (chatCounterSlice.payloadAction === 'updateMessageStatus') {
            setMessageList((prev: any[]) => chatHelper.updateChatStatus(prev, userIdRef.current));
        }
        dispatch(completeRealoadMessage());
    };

    useEffect(() => {
        if (pageInfo.page === 1) {
            chatHelper.scrollToBottom();
        } else {
            focusRef.current?.scrollIntoView();
            focusRef.current = null;
        }
    }, [messageList]);

    const loadChat = async (pageInfo: ChatHistoryQuery) => {
        try {
            dispatch(startFetching());
            if (chatCounterSlice.chatRoom) {
                await load_chat_data(
                    pageInfo,
                    chatCounterSlice.chatRoom.chatRoomId,
                    chatCounterSlice.chatRoom.memberId,
                );
            } else {
                const roomResponse = await props.chatUseCase.getChatRoom();
                if (roomResponse.success) {
                    dispatch(setChatRoom(roomResponse.data));
                    await load_chat_data(
                        pageInfo,
                        roomResponse.data?.chatRoomId,
                        roomResponse.data.memberId,
                    );
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(completeFetching());
        }
    };

    const getUserInfo = async () => {
        try {
            const user = props.userStorageUseCase.getUserInfo();
            userIdRef.current = user?.user?.id;
            setUserLocal(user ?? undefined);
        } catch (error: any) {
            openErrorNoti();
            dispatch(completeFetching());
        }
    };

    const addMessages = (messages: MessageEntity[]) => {
        setMessageList((prev: MessageEntity[]) => {
            const newMessageList: MessageEntity[] = chatHelper.removeDuplicateMessage(
                prev,
                messages,
            );
            return [...prev, ...newMessageList];
        });
    };

    const addMessageFirst = (messageList: MessageEntity[]) => {
        setMessageList((prev: MessageEntity[]) => {
            const newMessageList: MessageEntity[] = chatHelper.removeDuplicateMessage(
                prev,
                messageList,
            );
            return [...newMessageList, ...prev];
        });
    };

    const update_chat_status_server = async (chatRoomId: number, message_list: any[]) => {
        const promiseList: any[] = [];
        message_list.map((message) => {
            const data = {
                chatMessageId: message.chatMessageId,
                messageStatus: message.messageStatus,
            };
            promiseList.push(props.chatUseCase.postMessageStatus(data));
        });
        await Promise.all(promiseList);
        update_chat_status_socket(chatRoomId, message_list);
    };

    const update_chat_status_socket = async (chatRoomId: number, message_list: any[]) => {
        message_list.map((item: MessageEntity) => {
            const data = {
                action: 'updateMessageStatus',
                memberId: userIdRef.current,
                memberType: 0,
                chatRoomId: chatRoomId,
                chatMessageId: item.chatMessageId,
                chatMessage: item.messageStatus,
            };
            dispatch(sendSocket(data));
        });
    };

    const load_chat_data = async (pageInfo: any, chatRoomId: number, memberId: string) => {
        const data: ChatHistoryQuery = {
            ...pageInfo,
            chatRoomId: chatRoomId || -1,
        };
        const response = await props.chatUseCase.getChatHistory(data);
        const chatData: MessageEntity[] = response.data;
        if (chatData.length == 0) return;
        if (chatData.length < pageInfo.pageSize) {
            setMaxPage(pageInfo.page);
        }
        if (pageInfo.page === 1) {
            addMessages(chatData.reverse());
        } else {
            focusRef.current = document.getElementById(
                `message-id_${messageList[0].chatMessageId}`,
            );
            addMessageFirst(chatData.reverse());
        }
        const unread_messages = chatHelper.getUnreadMessage(chatData, memberId);
        if (unread_messages[0]) update_chat_status_server(chatRoomId, unread_messages);
    };

    const send_chat_message_server = async (chatRoomId: number, messageInfo: any) => {
        try {
            const data = {
                chatRoomId: chatRoomId,
                chatMessage: messageInfo.chatMessage,
            };
            const response = await props.chatUseCase.postMessage(data);
            if (response) {
                const chatData: MessageEntity = {
                    action: 'sendMessage',
                    chatRoomId: response?.data.chatRoomId,
                    chatMessageId: response?.data.chatMessageId,
                    chatMessage: response?.data.chatMessage,
                    memberId: userIdRef.current,
                    memberType: 0,
                    memberName: response?.data.memberName,
                    sendAt: response?.data.sendAt,
                    fileUrl: response?.data.fileUrl,
                    messageType: response?.data.messageType,
                    messageStatus: CHAT_STATUS.SEND,
                };
                send_chat_message_socket(chatData);
                updateChatSendingStatus(messageInfo.chatMessageId, chatData);
            }
        } catch (err) {
            updateChatSendingStatus(messageInfo.chatMessageId, {
                ...messageInfo,
                messageStatus: CHAT_STATUS.NOT_SEND,
                id: messageInfo.chatMessageId,
                chatMessage: messageInfo.chatMessage,
            });
        }
        chatHelper.scrollToBottom();
    };

    function send_chat_message_socket(data: MessageEntity, fileUrl?: string, messageType?: number) {
        dispatch(
            sendSocket({
                ...data,
                fileUrl: fileUrl ? fileUrl : null,
                messageType: messageType || 0,
            }),
        );
    }

    const sendFileMessage = async (fileUrl: string, file: File, data: any) => {
        const messageInfo: MessageEntity = {
            action: 'sendMessage',
            chatRoomId: chatCounterSlice.chatRoom?.chatRoomId ?? -1,
            chatMessageId: 'sending_' + messageList.length,
            chatMessage: file.name,
            memberId: userIdRef.current,
            memberType: 0,
            sendAt: '',
            messageType: 2,
            memberName: userLocal?.user?.nickname ?? '',
            fileUrl: fileUrl,
            messageStatus: CHAT_STATUS.SENDING,
        };
        setMessageList((prev: any) => [...prev, messageInfo]);
        try {
            const response = await props.chatUseCase.postUploadFile(data);
            if (response.success) {
                const message: MessageEntity = {
                    action: 'sendMessage',
                    chatRoomId: response?.data.chatRoomId,
                    chatMessageId: response?.data.chatMessageId,
                    chatMessage: response?.data.chatMessage,
                    memberId: userIdRef.current,
                    memberType: 0,
                    memberName: response?.data.memberName,
                    sendAt: response?.data.sendAt,
                    fileUrl: response?.data.fileUrl,
                    messageType: response?.data.messageType,
                    messageStatus: CHAT_STATUS.SEND,
                };
                send_chat_message_socket(
                    message,
                    response.data.fileUrl,
                    response?.data.messageType,
                );
                updateChatSendingStatus(messageInfo.chatMessageId + '', message);
            }
        } catch (err) {
            updateChatSendingStatus(messageInfo.chatMessageId + '', {
                ...messageInfo,
                id: messageInfo.chatMessageId,
                message: messageInfo.chatMessage,
                messageStatus: CHAT_STATUS.NOT_SEND,
            });
        }
    };

    const updateChatSendingStatus = (chatId: string, message: any) => {
        setMessageList((prev: any[]) => chatHelper.updateChatSendingStatus(prev, chatId, message));
    };

    const onSubmit = async (values: any) => {
        if (!values.message || values.message?.trim().length === 0) return;
        const messageInfo: MessageEntity = {
            action: 'sendMessage',
            chatRoomId: chatCounterSlice.chatRoom?.chatRoomId ?? -1,
            chatMessageId: 'sending_' + messageList.length,
            chatMessage: values.message,
            memberId: userIdRef.current,
            memberType: 0,
            sendAt: '',
            messageType: 0,
            memberName: userLocal?.user?.nickname ?? '',
            fileUrl: '',
            messageStatus: CHAT_STATUS.SENDING,
        };
        setMessageList((prev: any) => [...prev, messageInfo]);
        if (chatCounterSlice.connected && chatCounterSlice.chatRoom) {
            send_chat_message_server(chatCounterSlice.chatRoom.chatRoomId, messageInfo);
        } else {
            updateChatSendingStatus(messageInfo.chatMessageId + '', {
                ...messageInfo,
                id: messageInfo.chatMessageId,
                message: messageInfo.chatMessage,
                messageStatus: CHAT_STATUS.NOT_SEND,
            });
        }
        form.resetFields();
    };

    const resendMessage = (id: string, message: string) => {
        const messageNotResend = messageList.find((item) => item.chatMessageId == id);
        if (!messageNotResend) return;
        messageNotResend.messageStatus = CHAT_STATUS.SENDING;

        if (chatCounterSlice.connected && chatCounterSlice.chatRoom) {
            send_chat_message_server(chatCounterSlice.chatRoom.chatRoomId, messageNotResend);
        } else {
            updateChatSendingStatus(messageNotResend.chatMessageId + '', {
                ...messageNotResend,
                id: messageNotResend.chatMessageId,
                message: messageNotResend.chatMessage,
                messageStatus: CHAT_STATUS.NOT_SEND,
            });
        }
    };

    const handleScroll = (e: any) => {
        const ele = e.target;
        if (ele.scrollTop === 0 && (maxPage === -1 || pageInfo.page < maxPage)) {
            setPageInfo((prev: any) => ({
                ...prev,
                page: prev.page + 1,
            }));
        }
    };
    //load when page info change
    React.useEffect(() => {
        //ignore init load
        if (pageInfo.page === 1) return;
        reloadChatData(pageInfo);
    }, [pageInfo]);

    const reloadChatData = async (pageInfo: any) => {
        try {
            const chatRoomId = chatCounterSlice.chatRoom?.chatRoomId;
            if (!chatRoomId) return;
            try {
                await load_chat_data(pageInfo, chatRoomId, userIdRef);
            } catch (err) {
                console.log(err);
            }
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="CU-012-01-container"
        >
            <PageTitle title={<HeaderComponent />} />
            <section className="body">
                <ChatMessage
                    messageList={messageList}
                    resendMessage={resendMessage}
                    userLocal={userLocal}
                    handleScroll={handleScroll}
                />
                <ChatForm form={form} onSubmit={onSubmit} sendFileMessage={sendFileMessage} />
            </section>
        </div>
    );
};

export default CU01201Page;
